import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type { Ability } from '@/lib/permissions';
import type {
  ClientDeliverableFillColours,
  ClientDeliverableFillKey,
  ClientDeliverableSheetSettings,
} from '@/lib/types';
import {
  Button,
  ColorInput,
  EmptyState,
  Field,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * THE COLOURS THE CLIENT-DELIVERABLES MIRROR PAINTS ITS TAB WITH.
 *
 * One row fill per billing status, plus the fill for rows somebody added by
 * hand. Read and written through `GET | PUT /client-deliverables/sheet/sort` —
 * the endpoint the tracker's "Sort by" control already uses, which gained the
 * colours rather than being given a segment of its own.
 *
 * ⚠️ **THE WRITE IS A DIFF AND MUST STAY ONE.** An omitted key leaves a colour
 * alone, a hex sets one, and `null` resets one to its compiled default. Spreading
 * the loaded map back is a `200` that turns every untouched default into a stored
 * choice — invisible until a release moves a default and nothing follows it.
 *
 * ⚠️ **THIS DOES NOT REPAINT THE SHEET**, and nothing here pretends otherwise:
 * the tab is recoloured on the mirror's next run. No interval is quoted anywhere
 * in this file — the tick is a floor that stretches on failure, it lives in one
 * constant in the backend worker, and this codebase has already had "five
 * minutes" spread through a module whose worker says ten.
 */

/* --- the keys, and what they are called ---------------------------------- */

/*
 * 🔴 **A SECOND COPY OF THE TRACKER'S `BILLING_LABELS`, KNOWINGLY, AND IT WANTS
 * PROMOTING.** The live copy is module-local to `ClientDeliverablesPage.tsx` and
 * is not exported, so there is no way to read it from here without editing that
 * file — which this change is not allowed to touch. **The exit is one move:**
 * lift the map into a shared module under `@/lib`, have the tracker and this card
 * both import it, and delete the copy below.
 *
 * ⚠️ **Until then the risk is a WORDING DRIFT, not a broken build.** `tsc` cannot
 * see that two maps are meant to agree, so the failure is one screen calling a
 * state "Quote sent" and another calling it something else, with nothing failing.
 * The seven billing labels here are byte-identical to the tracker's as of
 * 2026-09-13 — **re-read that file before changing any of them.**
 *
 * ⚠️ **`standalone` IS THE ONE LABEL THAT IS NOT THE TRACKER'S**, because the
 * tracker's word for that population is `Standalone`, which says nothing on its
 * own. What a reader needs here is what the row IS — work somebody typed in with
 * no quote and no invoice behind it, which is what the tracker's own
 * "Add deliverable with no document" button creates.
 */
const FILL_LABELS: Record<ClientDeliverableFillKey, string> = {
  none: 'No document',
  not_billable: 'Not billable',
  quote_sent: 'Quote sent',
  awaiting_invoice: 'Awaiting invoice',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
  paid: 'Paid',
  standalone: 'Added by hand, with no quote or invoice',
};

/*
 * ⚠️ **Least to most advanced, matching the tracker's own `BILLING_VALUES` and
 * the server's `BILLING_STATUSES`.** The order is not cosmetic there — it is how
 * the furthest point reached across several invoices is picked — so a reader who
 * knows the tracker meets the same sequence here.
 *
 * ⚠️ **`none` IS FIRST AND IS THE MAJORITY STATE.** It is the plain row fill
 * every other colour is read against, and it means *no document of any kind*,
 * never *no colour configured*. It is offered like the rest: a key the server
 * ships and a screen hides is a setting nobody can discover.
 */
const BILLING_FILL_KEYS: readonly ClientDeliverableFillKey[] = [
  'none',
  'not_billable',
  'quote_sent',
  'awaiting_invoice',
  'invoiced',
  'cancelled',
  'paid',
];

/*
 * Separated from the seven above because it answers a DIFFERENT QUESTION —
 * where the row came from, not how far it has been billed — and the two groups
 * carry their own headings for exactly that reason. Without the split, "No
 * document" and "Added by hand, with no quote or invoice" sit adjacent and read
 * as two spellings of one thing.
 */
const SOURCE_FILL_KEYS: readonly ClientDeliverableFillKey[] = ['standalone'];

const ALL_FILL_KEYS: readonly ClientDeliverableFillKey[] = [
  ...BILLING_FILL_KEYS,
  ...SOURCE_FILL_KEYS,
];

/**
 * The path a `422` about one colour arrives under.
 *
 * ⚠️ **Built in ONE place because two independent things have to agree on it
 * character for character**: the key this form marks a control by, and the
 * write-key list `ApiError.routeFieldErrors()` partitions the server's refusal
 * against. A mismatch is silent in the worst direction — the message lands in
 * the banner, marked on no control, while still reading as though a field below
 * were highlighted.
 */
function fillErrorKey(key: string): string {
  return `fillColours.${key}`;
}

/* --- reading what the server sent ---------------------------------------- */

/**
 * One colour out of a map the server may not have sent at all.
 *
 * ⚠️ **Read through `unknown` rather than indexed straight into a control.** The
 * contract says the resolved map is complete; what that does not cover is a
 * server older than the colours (no map), or newer than this build (a key this
 * union has never heard of). A missing value bound to `value` renders an
 * uncontrolled input that silently stops reflecting anything.
 */
function colourAt(
  map: ClientDeliverableFillColours | undefined,
  key: ClientDeliverableFillKey,
): string | null {
  if (!map) return null;
  const raw: unknown = map[key];
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}

/**
 * The keys this build can actually offer — its own list, narrowed to what the
 * server reported a colour for.
 *
 * ⚠️ **Iterating OUR list rather than the response's keys is deliberate, and it
 * is safe precisely because the write is partial.** A ninth key a newer server
 * ships is not rendered here and is therefore not touched by any save: an
 * omitted key is left alone. Iterating the response instead would render a
 * control with no label and no meaning.
 */
function offeredKeys(settings: ClientDeliverableSheetSettings): ClientDeliverableFillKey[] {
  return ALL_FILL_KEYS.filter((key) => colourAt(settings.fillColours, key) !== null);
}

/* --- gating -------------------------------------------------------------- */

/**
 * The sentence a reader who may not change the colours sees.
 *
 * ⚠️ **THE ADMIN TIER IS THE FLOOR AND THE PERMISSION IS A NARROWER FILTER ON
 * TOP** — the order the mailer card above states at length. `PUT
 * /client-deliverables/sheet/sort` is admin-only, so a set granting
 * `deliverables.update` to a `basic_user` must not open this: `denialReason`
 * would answer `null` for them, which is why it is consulted only AFTER the tier
 * and why its `null` falls through to the admin sentence rather than to an
 * enabled control.
 *
 * ⚠️ **THE READ IS OPEN TO EVERY ROLE AND THE VALUES ARE NOT HIDDEN.** A tab
 * carries no record of the colours that produced it, so gating the display would
 * leave what is on screen unattributable to everybody except the few who may
 * change it. This is the DocuSign `viewer` shape — a refusal about THIS reader —
 * not the Xero `sync` one, where the only outcome is an error for everybody and
 * the affordance is withheld entirely.
 */
const FILL_COLOURS_DENIAL =
  'Your permission set does not include this. An administrator can change that.';

function fillColoursDenial(ability: Ability): string | null {
  /*
   * 2026-10-01: the set decides. The sheet routes' writes are
   * `requireGrant('deliverables', 'update')` (measured in
   * `client-deliverables-sheet.routes.ts`). *SUPERSEDED: `if (ability.isAdmin)
   * return null;`.*
   */
  if (ability.canUpdate('deliverables')) return null;
  /*
   * The helper's own sentence where it has a truer one — a `viewer` is not one
   * demotion away from this control but structurally unable to write anything,
   * and "administrators only" would send them to ask for the wrong thing.
   */
  return denialReason(ability, 'update', 'deliverables') ?? FILL_COLOURS_DENIAL;
}

/* --- what a save comes to ------------------------------------------------ */

/**
 * ⚠️ **THE CONSEQUENCE IS CARRIED HERE, IN THE OUTCOME, AND NOT AS A STANDING
 * LINE UNDER THE CONTROLS — and that is a deliberate match of the sort control
 * rather than an omission.** `TrackerSheetPanel` used to render *"Sorting
 * changes this sheet for everyone."* permanently beside its dropdown; **the
 * owner removed it on 2026-09-05** as a consequence note rather than a refusal,
 * and what survived there is exactly this: the denial, and a sentence reporting
 * what the last save came to. Restoring a standing line here would reverse that
 * ruling on a neighbouring control.
 *
 * ⚠️ **NO INTERVAL IS QUOTED.** "The next sync" is true whatever the worker's
 * constant says; a number here would be wrong precisely when it mattered.
 *
 * ⚠️ **It says "will be", never "has been".** Nothing in this card calls
 * `POST /client-deliverables/sheet/sync`, so no repaint has been observed — and
 * reporting one that was never watched is this codebase's most repeated mistake.
 */
const SAVED = 'Saved. Everyone sees the same sheet — it will be recoloured on the next sync.';

/* --- the card ------------------------------------------------------------ */

/** The empty-state glyph: a palette. */
function PaletteIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M12 3a9 9 0 0 0 0 18h1.5a2 2 0 0 0 1.6-3.2 2 2 0 0 1 1.6-3.2H19a2.6 2.6 0 0 0 2-4.3A9 9 0 0 0 12 3Z" />
      <circle cx="7.8" cy="12.2" r="1" />
      <circle cx="9.6" cy="8.4" r="1" />
      <circle cx="13.6" cy="7.2" r="1" />
    </svg>
  );
}

export function SheetFillColoursCard() {
  const { ability } = useAuth();
  const headingId = useId();
  const billingGroupId = useId();
  const sourceGroupId = useId();

  const [settings, setSettings] = useState<ClientDeliverableSheetSettings | null>(null);
  /**
   * The colours AS LOADED — the baseline every diff is taken against, and never
   * the same object as `draft`. Re-seeded from the save RESPONSE rather than
   * from what was sent, so the box shows what is stored rather than what was
   * typed the moment anything normalises on the way down.
   */
  const [loaded, setLoaded] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  /**
   * Keys the reader has pressed Reset on.
   *
   * ⚠️ **A THIRD STATE, AND IT CANNOT BE FOLDED INTO `draft`.** Reset sends an
   * explicit `null`, which is *go back to the compiled default* — a different
   * instruction from *store this hex*, even when the hex IS the default today.
   * Writing the default's value instead would store it as a choice, so the
   * colour would stop following the default the next time a release moved one.
   */
  const [resets, setResets] = useState<ReadonlySet<ClientDeliverableFillKey>>(new Set());

  const [loading, setLoading] = useState(true);
  /**
   * ⚠️ **THE TITLE TRAVELS WITH THE MESSAGE, rather than being a literal at the
   * render site.** One banner serves the read and the write, and the two want
   * opposite sentences: "couldn't save" over a failed READ sends somebody
   * hunting for a save they never made. A single `string` here made the title a
   * guess about which call had failed.
   */
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const seed = useCallback((data: ClientDeliverableSheetSettings) => {
    const keys = offeredKeys(data);
    const colours: Record<string, string> = {};
    const compiled: Record<string, string> = {};
    for (const key of keys) {
      const value = colourAt(data.fillColours, key);
      if (value !== null) colours[key] = value;
      /*
       * ⚠️ Falls back to the STORED colour, never to a literal. A default this
       * server did not report leaves Reset unoffered for that key — which is
       * honest — where a hardcoded `#ffffff` would offer a reset to a colour
       * nothing behind the control has ever agreed to.
       */
      compiled[key] = colourAt(data.fillColourDefaults, key) ?? (value ?? '');
    }
    setSettings(data);
    setLoaded(colours);
    setDraft(colours);
    setDefaults(compiled);
    setResets(new Set());
  }, []);

  const load = useCallback(
    (signal: AbortSignal) =>
      api
        .clientDeliverableSheetSort(signal)
        .then((response) => {
          if (signal.aborted) return;
          /*
           * ⚠️ `?? null` rather than `response.data` straight through. The
           * contract says `data` is always present; what that does not cover is
           * a `200` carrying a body we did not expect — an older server, a proxy
           * answering for it, a shape change. A resolved `undefined` read inside
           * a render is what React escalates to unmounting the tree.
           */
          const data = response.data ?? null;
          if (data === null) {
            setSettings(null);
            return;
          }
          seed(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          /*
           * ⚠️ **A 404 IS THE ORDINARY ANSWER FROM A SERVER PREDATING THIS
           * ROUTE**, not a fault — this app deploys separately from the API and
           * can be newer than it. It leaves `settings` null, which renders the
           * empty state below rather than a red banner about a mirror nobody
           * asked after.
           *
           * A 401 belongs entirely to `@/lib/api`, which attempts one refresh and
           * then ends the session; a message here would contradict it midway
           * through signing out.
           *
           * There is deliberately no 403 branch: the READ is open to every
           * authenticated role, so a 403 would mean the contract had changed
           * under us — which belongs where somebody will see it.
           */
          if (caught instanceof ApiError && caught.status === 404) return;
          if (caught instanceof ApiError && caught.status === 401) return;
          setError({
            title: 'Couldn’t read the sheet colours.',
            message:
              caught instanceof Error ? caught.message : 'The server did not answer.',
          });
        })
        .finally(() => {
          if (signal.aborted) return;
          setLoading(false);
        }),
    [seed],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const keys = useMemo(
    () => (settings ? offeredKeys(settings) : []),
    [settings],
  );

  /**
   * What will actually be sent — **one derivation, so the Save button's enabled
   * state and the payload can never disagree.** Two of them is how a Save that
   * is offered sends `{}`, which this API answers `422`.
   *
   * ⚠️ **Reset is checked FIRST.** A key that has been reset AND differs from
   * what was loaded must still go as `null`; sending its hex would store the
   * default as a choice, which is the one thing Reset exists not to do.
   */
  const patch = useMemo(() => {
    const next: Record<string, string | null> = {};
    for (const key of keys) {
      if (resets.has(key)) {
        next[key] = null;
        continue;
      }
      const value = draft[key];
      if (value !== undefined && value !== loaded[key]) next[key] = value;
    }
    return next;
  }, [keys, draft, loaded, resets]);

  const changedKeys = Object.keys(patch);
  const denial = fillColoursDenial(ability);

  function save() {
    // The double-fire guard as well as the disabled reason: set before the call
    // and cleared in `finally`, never on either branch alone.
    if (saving || settings === null || changedKeys.length === 0) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    setFieldErrors({});
    api
      /*
       * ⛔ **`sort` AND `order` ARE DELIBERATELY NOT SENT, AND RE-ADDING THEM IS
       * A LOST UPDATE.** *SUPERSEDED, and it was true for about an hour: "`sort`
       * AND `order` ARE ECHOED FROM THE LAST RESPONSE, because the body schema
       * has declared both required since the ordering was the only thing here …
       * Drop the pair the day the server makes it optional."* **That day was the
       * same day** — the server now takes all three keys independently, and an
       * omitted one is left untouched with no statement issued for it.
       *
       * ⚠️ **THE ECHO LOOKS HARMLESS, WHICH IS WHY IT NEEDS THE PROHIBITION.**
       * The values came from the server moments earlier, so re-sending them
       * reads as a no-op. It is not: this is ONE row shared by every
       * administrator, so a colour save carrying a stale ordering REVERTS a sort
       * somebody else chose in between — with a `200`, and nothing on screen
       * saying so. The failure is invisible from this form and shows up on a
       * spreadsheet ten minutes later.
       *
       * ⚠️ **`patch` is a DIFF and is never the whole loaded map.** An omitted
       * key leaves that colour alone; a `null` resets one to its compiled
       * default. Spreading every loaded colour back would write each one as an
       * explicit choice, including the ones nobody has touched — which is the
       * same class of silent write as the echo above.
       *
       * ⚠️ **No `AbortSignal`, deliberately** — the method accepts one, and the
       * house rule exists so a component's fetch does not outlive it. A `PUT`
       * aborted on unmount would leave the stored colours in an unknown state
       * with nobody to tell, which is the worse failure. Same ruling as
       * `useTrackerSheetSort`'s own write.
       */
      .setClientDeliverableSheetFillColours({ fillColours: patch })
      .then((response) => {
        const data = response.data ?? null;
        if (data === null) {
          /*
           * ⚠️ **NOT worded as a failed save.** The `PUT` returned, so the
           * colours ARE stored; what is missing is the body to re-seed from.
           * Telling somebody their save failed when it did not is the lie they
           * act on — by saving again.
           */
          setError({
            title: 'Saved, but there is nothing to show.',
            message:
              'The colours were stored. The server sent no settings back, so what is on screen may be out of date — reload to see them.',
          });
          return;
        }
        // Re-seeded from the RESPONSE, which is the only place a normalisation
        // on the way down is visible — and it clears the reset marks, because
        // what came back is now what is stored.
        seed(data);
        setSaved(true);
      })
      .catch((caught: unknown) => {
        if (!(caught instanceof ApiError)) {
          setError({
            title: 'Couldn’t save the sheet colours.',
            message: caught instanceof Error ? caught.message : 'The server did not answer.',
          });
          return;
        }
        /*
         * ⚠️ **The known-key list is derived from the KEYS BEING SENT**, so it
         * cannot drift from the controls on screen. Anything else the server
         * names — a bare `fillColours`, the echoed `sort` or `order`, an
         * unrecognised key — binds to no control here and goes to the banner
         * rather than to an off-screen input, where it would inflate a "one
         * field needs attention" count and highlight nothing.
         *
         * A 403 is a live possibility rather than a formality: the gate above is
         * an optimisation and a role can be taken away under a token that lasts
         * over five days. It carries no `details`, so it lands here as a banner.
         */
        const routed = caught.routeFieldErrors(changedKeys.map(fillErrorKey));
        setFieldErrors(routed.fields);
        if (routed.unroutable.length > 0) {
          setError({ title: 'Couldn’t save the sheet colours.', message: routed.unroutable.join(' ') });
        } else if (Object.keys(routed.fields).length === 0) {
          setError({ title: 'Couldn’t save the sheet colours.', message: caught.message });
        }
      })
      .finally(() => setSaving(false));
  }

  function change(key: ClientDeliverableFillKey, value: string) {
    setSaved(false);
    setFieldErrors((current) => {
      if (current[fillErrorKey(key)] === undefined) return current;
      const next = { ...current };
      delete next[fillErrorKey(key)];
      return next;
    });
    // Typing clears the reset mark: the reader has chosen a colour, which is a
    // different instruction from "go back to the default".
    setResets((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function reset(key: ClientDeliverableFillKey) {
    setSaved(false);
    setResets((current) => new Set(current).add(key));
    // The swatch shows the default immediately; the payload still sends `null`.
    // See `patch`, which checks the reset mark before the value.
    setDraft((current) => ({ ...current, [key]: defaults[key] ?? current[key] ?? '' }));
  }

  function renderKey(key: ClientDeliverableFillKey) {
    const value = draft[key] ?? '';
    const fallback = defaults[key];
    /*
     * ⚠️ **Reset is offered only where the current value DIFFERS FROM THE
     * DEFAULT**, which is also what keeps `patch` honest: a reset on a key
     * already at its default would send `null` for a colour that looks
     * unchanged, turning "stored as this colour" into "unset" with nothing on
     * screen moving. Withheld, rather than shown inert, where there is no
     * default to go back to.
     */
    const resettable = fallback !== undefined && fallback !== '' && fallback !== value;
    const message = fieldErrors[fillErrorKey(key)];
    /*
     * ⚠️ **WRAPPED IN A `Field`, WHICH IS THE PRIMITIVE'S OWN ARRANGEMENT.**
     * `ColorInput` renders no visible label and no error of its own: `label` is
     * the accessible name for the swatch and the reset button — the two controls
     * a `Field`'s single `<label htmlFor>` cannot reach — while the `<label>`,
     * the error and the `aria-describedby` wiring come from the `Field`. Passing
     * the label without the wrapper leaves the hex box unlabelled on screen.
     *
     * ⚠️ **`resetLabel` IS LEFT AT ITS DEFAULT**, which is the visible word
     * "Reset". The primitive already builds the accessible name as
     * `Reset: <label>`, so eight buttons announce eight different colours; a
     * sentence passed here would be rendered, not announced.
     */
    return (
      <Field key={key} label={FILL_LABELS[key]} error={message}>
        {({ id, 'aria-describedby': describedBy }) => (
          <ColorInput
            id={id}
            aria-describedby={describedBy}
            label={FILL_LABELS[key]}
            value={value}
            disabled={denial !== null || saving}
            invalid={message !== undefined}
            onChange={(next) => change(key, next)}
            {...(resettable ? { onReset: () => reset(key) } : {})}
          />
        )}
      </Field>
    );
  }

  const billingKeys = keys.filter((key) => BILLING_FILL_KEYS.includes(key));
  const sourceKeys = keys.filter((key) => SOURCE_FILL_KEYS.includes(key));

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Client deliverables sheet colours" />
      <PanelBody className="flex flex-col gap-4">
        {/*
         * The same shape as `SettingsPage`'s local `Notice`, written out rather
         * than imported: that one is module-local to the page and its own
         * comment names a THIRD caller as the point to promote it to `ui/`.
         * This is that third caller — but `ui/` is being changed by another hand
         * right now, so the promotion is recorded here rather than raced.
         */}
        {error && (
          <Notice tone="danger" role="alert" title={error.title}>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error.message}</span>
          </Notice>
        )}

        {loading && settings === null ? (
          /* Skeletons in the real shape — a label and a swatch, eight times. */
          <div className="flex flex-col gap-3">
            {Array.from({ length: ALL_FILL_KEYS.length }, (_, row) => (
              <div key={row} className="flex items-center gap-3">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-9 w-24" />
              </div>
            ))}
          </div>
        ) : keys.length === 0 && error !== null ? (
          /*
           * ⚠️ **A FAILED READ GETS THE BANNER AND NOTHING ELSE.** The empty
           * state below is a positive claim about the SERVER — that it has no
           * colours to offer — and a request that never came back is not
           * evidence for it. Rendering both put a confident wrong sentence
           * under a red banner saying the opposite.
           */
          null
        ) : keys.length === 0 ? (
          /*
           * ⚠️ **"This server does not offer them", never "they are not set".**
           * The resolved map is always complete when it is sent at all, so an
           * absent one means a server older than the colours — version skew,
           * which is an ordinary thing to be in the middle of and not a fault to
           * report in red.
           */
          <EmptyState
            icon={<PaletteIcon />}
            title="This API has no sheet colours to offer"
            description="The server is running a build older than this one. The mirror paints its own colours until it catches up."
          />
        ) : (
          <>
            <div role="group" aria-labelledby={billingGroupId} className="flex flex-col gap-3">
              {/*
               * A group LABEL, not helper text — it names what the rows in it
               * are, which is the one thing that stops "No document" reading as
               * "no colour has been chosen" beside "Added by hand".
               */}
              <p
                id={billingGroupId}
                className="m-0 text-xs font-medium text-fg-subtle"
              >
                Billing status
              </p>
              {billingKeys.map(renderKey)}
            </div>

            {sourceKeys.length > 0 && (
              <div
                role="group"
                aria-labelledby={sourceGroupId}
                className="flex flex-col gap-3 border-t border-border pt-4"
              >
                <p
                  id={sourceGroupId}
                  className="m-0 text-xs font-medium text-fg-subtle"
                >
                  Where the row came from
                </p>
                {sourceKeys.map(renderKey)}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
              <Button
                onClick={save}
                disabled={denial !== null || saving || changedKeys.length === 0}
                title={denial ?? undefined}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {/*
               * The reason is text as well as a `title`: a disabled button has
               * `pointer-events: none`, so its tooltip never appears for the
               * person it is written for — and without it a greyed control makes
               * a permission denial and a fault identical.
               */}
              {denial && <p className="m-0 text-xs text-fg-subtle">{denial}</p>}
              {saved && !denial && (
                <p role="status" className="m-0 text-xs text-fg-subtle">
                  {SAVED}
                </p>
              )}
            </div>
          </>
        )}
      </PanelBody>
    </Panel>
  );
}
